import React, { useMemo, useState } from 'react';
import { Box, Typography, Rating, Button, CircularProgress, Menu, MenuItem, ButtonBase } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import StarIcon from '@components/Icon/Star';
import StarActiveIcon from '@components/Icon/StarActiveIcon';
import CheckIcon from '@mui/icons-material/Check';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import { useJobApplicationsOperations } from '@modules/jobApplications/context/JobApplicationsOperationsContext';
import {
    JobApplicationStatus,
    jobApplicationStatusLabels,
    jobApplicationSubStatusLabels,
    jobApplicationStatuses,
} from '@modules/jobApplications/statuses';
import { getStatusColors } from '@modules/jobApplications/utils';
import { useTalentOperations } from '@modules/talents/context/TalentOperationsContext';
import AvatarStatusFavorite from '@modules/talents/components/AvatarStatusFavorite';
import { AvatarWithStatusProps } from '@modules/talents/components/AvatarWithStatus';

interface FloatingSelectionBarProps {
    selectedTalents: any[];
    onClose: () => void;
    onSendMessage: () => void;
}

const FloatingContainer = styled(Box)(({ theme }) => ({
    position: 'fixed',
    bottom: '24px',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: theme.palette.mode === 'dark' ? '#1E2124' : '#282C30',
    borderRadius: '24px',
    padding: '12px 20px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.4)',
    zIndex: 1000,
    border: `1px solid ${theme.palette.divider}`,
    minWidth: '400px',
    maxWidth: '90vw',
}));

const Divider = styled(Box)({
    width: '1px',
    height: '32px',
    backgroundColor: '#404040',
    flexShrink: 0,
});

// Status items for the status menu — excluding Rejected (handled via subStatus)
const ALL_STATUSES = Object.values(JobApplicationStatus).filter(
    s => s !== JobApplicationStatus.Rejected
);

const FloatingSelectionBar: React.FC<FloatingSelectionBarProps> = ({
    selectedTalents,
    onClose,
}) => {
    const theme = useTheme();
    const {
        updateSelectedHiringStatus,
        updateSelectedRating,
        isUpdatingStatus,
        isUpdatingRating,
        currentStatus,
    } = useJobApplicationsOperations();
    const {
        updateStatus: updateTalentStatus,
        isUpdatingStatus: isTalentStatusUpdating,
        isUpdatingFavorites,
    } = useTalentOperations();

    // Derive talent IDs and favorites map from selected job applications
    const talentIds = useMemo(
        () => selectedTalents.map((app: any) => app.talent?._id).filter(Boolean) as string[],
        [selectedTalents]
    );
    const talentFavoritesMap = useMemo(
        () =>
            selectedTalents.reduce<Record<string, Array<{ _id: string; type: string; upercutId?: string; createdAt: string }>>>(
                (map, app: any) => {
                    const tid = app.talent?._id;
                    if (tid) map[tid] = app.talent?.upercutFavorites ?? [];
                    return map;
                },
                {}
            ),
        [selectedTalents]
    );
    const currentTalentStatus = useMemo((): AvatarWithStatusProps['status'] => {
        const statuses = selectedTalents
            .map((app: any) => app.talent?.currentStatusUser)
            .filter(Boolean) as string[];
        const unique = Array.from(new Set(statuses));
        if (unique.length === 0) return 'nothing';
        if (unique.length === 1) return unique[0] as AvatarWithStatusProps['status'];
        return 'mixed';
    }, [selectedTalents]);

    const handleTalentStatusChange = async (newStatus: string) => {
        if (!isTalentStatusUpdating && talentIds.length > 0) {
            await updateTalentStatus(talentIds, newStatus);
        }
    };

    // Status menu state
    const [statusAnchor, setStatusAnchor] = useState<null | HTMLElement>(null);
    const [drillStatus, setDrillStatus] = useState<string | null>(null);

    const handleOpenStatusMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
        setStatusAnchor(e.currentTarget);
        setDrillStatus(null);
    };
    const handleCloseStatusMenu = () => {
        setStatusAnchor(null);
        setDrillStatus(null);
    };
    const handleStatusClick = (status: string) => {
        const subs = jobApplicationStatuses[status as keyof typeof jobApplicationStatuses] ?? [];
        if (subs.length === 0) {
            handleSubmitStatus(status, '');
        } else {
            setDrillStatus(status);
        }
    };
    const handleSubStatusClick = (subStatus: string) => {
        if (drillStatus) handleSubmitStatus(drillStatus, subStatus);
    };
    const handleSubmitStatus = async (status: string, subStatus: string) => {
        handleCloseStatusMenu();
        await updateSelectedHiringStatus(status, subStatus);
    };

    // Rating handler
    const handleRatingChange = async (_: React.SyntheticEvent, value: number | null) => {
        if (value == null) return;
        await updateSelectedRating(value);
    };

    if (selectedTalents.length === 0) return null;

    // Calculate average rating across selected applications
    const avgRating =
        selectedTalents.reduce(
            (sum, app) => sum + (app.upercutAverageRating ?? 0),
            0
        ) / selectedTalents.length;

    // Determine status chip appearance
    const statusColors = currentStatus && currentStatus !== 'mixed'
        ? getStatusColors(currentStatus)
        : null;
    const statusLabel = statusColors
        ? statusColors.label
        : selectedTalents.length === 1
            ? (jobApplicationStatusLabels as any)[currentStatus] ?? currentStatus
            : 'Statuts mixtes';

    const drillSubStatuses = drillStatus
        ? jobApplicationStatuses[drillStatus as keyof typeof jobApplicationStatuses] ?? []
        : [];
    const drillColors = drillStatus ? getStatusColors(drillStatus) : null;

    const isAnyLoading = isUpdatingStatus || isUpdatingRating || isUpdatingFavorites || isTalentStatusUpdating;

    return (
        <FloatingContainer>
            {/* Selection count badge */}
            <ButtonBase
                onClick={onClose}
                disabled={isAnyLoading}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    backgroundColor: theme.palette.primary.main,
                    borderRadius: '16px',
                    padding: '8px 16px',
                    color: theme.palette.neutralWhite?.main ?? '#fff',
                    flexShrink: 0,
                }}>
                <CheckIcon sx={{ fontSize: '18px' }} />
                <Typography fontWeight={600} fontSize="14px">
                    {selectedTalents.length} candidat{selectedTalents.length > 1 ? 's' : ''}
                </Typography>
            </ButtonBase>

            <Divider />

            {/* Talent status & favorites */}
            <AvatarStatusFavorite
                talentIds={talentIds}
                currentStatus={currentTalentStatus}
                upercutFavorites={talentFavoritesMap}
                onStatusChange={handleTalentStatusChange}
                shouldStopPropagation={true}
                disabled={isTalentStatusUpdating}
                loading={isTalentStatusUpdating}
                size="sm"
            />

            <Divider />

            {/* Hiring status update button */}
            <Button
                onClick={handleOpenStatusMenu}
                disabled={isUpdatingStatus}
                startIcon={isUpdatingStatus ? <CircularProgress size={12} color="inherit" /> : undefined}
                sx={{
                    borderRadius: '16px',
                    padding: '8px 16px',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '13px',
                    flexShrink: 0,
                    color: theme.palette.neutralWhite?.main ?? '#fff',
                    backgroundColor: statusColors ? statusColors.background : 'rgba(255,255,255,0.1)',
                    '&:hover': {
                        backgroundColor: statusColors ? statusColors.background : 'rgba(255,255,255,0.18)',
                        opacity: 0.85,
                    },
                    '&.Mui-disabled': { opacity: 0.5 },
                }}>
                {statusLabel}
            </Button>

            {/* Status menu */}
            <Menu
                anchorEl={statusAnchor}
                open={Boolean(statusAnchor)}
                onClose={handleCloseStatusMenu}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                {!drillStatus
                    ? ALL_STATUSES.map(status => {
                          const colors = getStatusColors(status);
                          return (
                              <MenuItem key={status} onClick={() => handleStatusClick(status)}>
                                  <Box
                                      sx={{
                                          width: 10,
                                          height: 10,
                                          borderRadius: '50%',
                                          backgroundColor: colors.background,
                                          mr: 1,
                                          flexShrink: 0,
                                      }}
                                  />
                                  <Typography fontSize="14px" sx={{ flex: 1 }}>
                                      {colors.label}
                                  </Typography>
                                  {(jobApplicationStatuses[status as keyof typeof jobApplicationStatuses] ?? []).length > 0 && (
                                      <ChevronRightIcon fontSize="small" sx={{ ml: 1, color: 'text.secondary' }} />
                                  )}
                              </MenuItem>
                          );
                      })
                    : [
                          <MenuItem key="__back" onClick={() => setDrillStatus(null)}>
                              <ArrowBackIcon fontSize="small" sx={{ mr: 1 }} />
                              <Box
                                  sx={{
                                      width: 10,
                                      height: 10,
                                      borderRadius: '50%',
                                      backgroundColor: drillColors?.background,
                                      mr: 1,
                                  }}
                              />
                              <Typography fontSize="14px" fontWeight={600}>
                                  {drillColors?.label}
                              </Typography>
                          </MenuItem>,
                          ...drillSubStatuses.map(sub => (
                              <MenuItem key={sub} onClick={() => handleSubStatusClick(sub)}>
                                  <Typography fontSize="14px" sx={{ pl: 3 }}>
                                      {jobApplicationSubStatusLabels[sub as keyof typeof jobApplicationSubStatusLabels]}
                                  </Typography>
                              </MenuItem>
                          )),
                      ]}
            </Menu>

            <Divider />

            {/* Bulk rating */}
            <Box
                sx={{
                    padding: '6px 12px',
                    borderRadius: '16px',
                    backgroundColor: 'rgba(255,255,255,0.07)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    flexShrink: 0,
                    opacity: isUpdatingRating ? 0.5 : 1,
                    pointerEvents: isUpdatingRating ? 'none' : 'auto',
                }}>
                {isUpdatingRating && <CircularProgress size={12} sx={{ color: '#ffd700', mr: 1 }} />}
                <Rating
                    value={avgRating}
                    sx={{ color: '#ffd700', display: 'flex', alignItems: 'center', gap: '8px' }}
                    icon={<StarActiveIcon style={{ width: '18px', height: '18px' }} />}
                    emptyIcon={<StarIcon style={{ width: '18px', height: '18px' }} />}
                    onChange={handleRatingChange}
                />
            </Box>

            {/* Close */}
            <Button
                onClick={onClose}
                disabled={isAnyLoading}
                sx={{
                    minWidth: 0,
                    padding: '6px',
                    borderRadius: '50%',
                    color: 'rgba(255,255,255,0.5)',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
                }}>
                <CloseIcon sx={{ fontSize: '18px' }} />
            </Button>
        </FloatingContainer>
    );
};

export default FloatingSelectionBar;
